import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// ICONS
import cogIcon from '../assets/gearIcon-32x32-1.png';
import graphIcon from '../assets/graphIcon-32x32-1.png';
import rocketIcon from '../assets/rocketIcon-32x32-1.png';
import subscriptionIcon from '../assets/subscriptionIcon-32x32-1.svg';
import logEngineIcon from '../assets/logEngine-32x32.svg';
// HOOKS
import { useRefreshToken } from '../hooks/useTokenManager';
// APIS
import { fetchPromptPreviewApi } from '../apis/data.api';
import { fetchTrialAccess } from '../apis/trial.api';
// REDUX
import { setUnityAppRunning, setCurrentPage } from '../redux/app.slice';
// Import the background image
import aiBackground from '../images/ai_background.png';

const APP_URL = process.env.REACT_APP_BASE_URL_LOC;
console.log('APP_URL', APP_URL, !APP_URL.includes('app') && 'not app deploy');

// Dev prompt-override editor config.
// Case studies map to the Unity negotiationID / convai case_study key.
const DEV_PROMPT_OVERRIDE_KEY = 'devPromptOverride';
const CASE_STUDIES = [
  'BadBreak', 'BadBreak2', 'BadBreak3', 'Bank', 'BeanMachine',
  'CIPSAdvancedProcurement', 'ControlTheConsultants', 'DeliciousChocolate',
  'Electrotec', 'InTheCan', 'MagicCleen', 'MountainView', 'Mycorp',
  'PerfectStorm', 'PresidentialSecurity', 'SmithChair', 'SuperiorTestingServices',
  'ToppTeam', 'TrustAirlines', 'WakeUpCall', 'WelcomeMotors',
];
const CASE_STUDY_AVATARS = ['Stefan', 'Tony'];

const Base = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user, shallowEqual);
  const { loginStatus, limitedAccess } = useSelector((state) => state.user);
  const { role, organization } = useSelector((state) => state.user.user);
  
  const { idToken, expiresAt, isRefreshing } = useSelector((state) => state.token);
  const [isHandlingTokenError, setIsHandlingTokenError] = useState(false);
  useRefreshToken();

  // The trial button is for an allowlist the server holds (TRIAL_MANAGER_EMAILS),
  // not a role, so ask. Any failure just means no button.
  const [canManageTrials, setCanManageTrials] = useState(false);
  useEffect(() => {
    if (!loginStatus || !idToken) return undefined;
    let cancelled = false;
    fetchTrialAccess(idToken)
      .then((d) => !cancelled && setCanManageTrials(!!(d && d.canManage)))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [loginStatus, idToken]);

  // ---- Dev prompt-override editor (dev-exclusive) ----
  // Gated on an env flag that is only set on the dev deployment, so the panel
  // never appears on stage/prod even if this code is present there.
  const isDevPromptTool =
    role === 'admin' && process.env.REACT_APP_ENABLE_PROMPT_OVERRIDE === 'true';
  const [ovCaseStudy, setOvCaseStudy] = useState('');
  const [ovAvatar, setOvAvatar] = useState('Stefan');
  const [ovOriginal, setOvOriginal] = useState('');
  const [ovPrompt, setOvPrompt] = useState('');
  const [ovLoading, setOvLoading] = useState(false);
  const [ovStatus, setOvStatus] = useState('');
  const [ovActive, setOvActive] = useState(() => {
    try {
      const raw = localStorage.getItem(DEV_PROMPT_OVERRIDE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const loadCurrentPrompt = async () => {
    if (!ovCaseStudy || !ovAvatar) {
      setOvStatus('Pick a case study and an avatar first.');
      return;
    }
    setOvLoading(true);
    setOvStatus('');
    try {
      const data = await fetchPromptPreviewApi(idToken, {
        caseStudy: ovCaseStudy,
        personality: ovAvatar,
      });
      const text = (data && data.system_message) || '';
      setOvOriginal(text);
      setOvPrompt(text);
      setOvStatus(text ? 'Loaded current prompt.' : 'No prompt found for that selection.');
    } catch (err) {
      setOvStatus(`Failed to load prompt: ${err.message}`);
    } finally {
      setOvLoading(false);
    }
  };

  // Any change from the loaded original counts as an override.
  const saveOverride = () => {
    if (!ovCaseStudy || !ovAvatar) {
      setOvStatus('Pick a case study and an avatar first.');
      return;
    }
    if (ovPrompt === ovOriginal) {
      setOvStatus('No change from the original — nothing to override. Edit the prompt first.');
      return;
    }
    const payload = {
      caseStudy: ovCaseStudy,
      personality: ovAvatar,
      original: ovOriginal,
      edited: ovPrompt,
    };
    localStorage.setItem(DEV_PROMPT_OVERRIDE_KEY, JSON.stringify(payload));
    setOvActive(payload);
    setOvStatus(`Override saved for ${ovAvatar} in ${ovCaseStudy}. Launch the app to use it.`);
  };

  const clearOverride = () => {
    localStorage.removeItem(DEV_PROMPT_OVERRIDE_KEY);
    setOvActive(null);
    setOvStatus('Override cleared. The app will use the original prompt.');
  };

  useEffect(() => {
    // console.log('BASE user', user);
    // console.log('BASE loginStatus', loginStatus);
    // console.log('BASE role', role);
    if (loginStatus && role) {
      dispatch(setCurrentPage('home'));
    } else {
      // dispatch(setCurrentPage('login'));
      // navigate('/login', { replace: true });
    }
  }, [loginStatus, role]);

  // Add debug logs
  console.log('Base component state:', {
    loginStatus,
    limitedAccess,
    role,
    user
  });

  // Check if access is limited (either by subscription or empty organization)
  const isAccessLimited = limitedAccess || !organization;

  const trialButton = canManageTrials && (
    <button
      onClick={() => navigate('/trial-accounts')}
      className="flex items-center gap-2 px-7 py-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow text-lg"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="#6c63ff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8" aria-hidden="true">
        <circle cx="9" cy="8" r="3.2" />
        <path d="M3 19c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
        <path d="M17 7v6M14 10h6" />
      </svg>
      Trial Accounts
    </button>
  );

  return (
    <div className="min-h-screen bg-white font-sans">
      <main className="p-10">
        <p className="text-[#6c63ff] mb-2">Welcome, {user.name || 'admin'}.</p>
        <h1 className="text-3xl mb-8">Your #1 negotiation training tool, powered by AI!</h1>
        
        {loginStatus && (
          <>
            {isAccessLimited && (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700">
                {role === 'admin' 
                  ? 'Note: There is no active subscription in this region.'
                  : !organization
                    ? 'Note: Your account is not associated with an organization. Please contact support.'
                    : 'Note: You don\'t have an active subscription in this region. Access is limited.'}
              </div>
            )}
            
            {(!isAccessLimited || role === 'admin') && (
              <div className="flex gap-5">
                {(role === 'admin' || role === 'leader' || role === 'orgLead') && (
                  <button 
                    onClick={() => navigate('/subscription')}
                    className="flex items-center gap-2 px-7 py-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow text-lg"
                  >
                    <img src={subscriptionIcon} alt="Subscription" className="w-8 h-8" />
                    Subscription
                  </button>
                )}
                <button 
                  onClick={() => navigate('/app')}
                  className="flex items-center gap-2 px-7 py-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow text-lg"
                >
                  <img src={rocketIcon} alt="Launch App" className="w-8 h-8" />
                  Launch App
                </button>
                {/* One button for everyone. What it opens depends on the role:
                    a trainee sees their own progress, a leader sees their
                    organisation, an admin sees every organisation. */}
                <button
                  onClick={() => navigate('/metrics')}
                  className="flex items-center gap-2 px-7 py-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow text-lg"
                >
                  <img src={graphIcon} alt="Metrics" className="w-8 h-8" />
                  Metrics
                </button>
                {/* Admin only: the anonymity grid is set by LavenirAI staff,
                    per organisation per region. */}
                {role === 'admin' && (
                  <button
                    onClick={() => navigate('/metrics/settings')}
                    className="flex items-center gap-2 px-7 py-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow text-lg"
                  >
                    <img src={cogIcon} alt="Metrics settings" className="w-8 h-8" />
                    Metrics Settings
                  </button>
                )}
                {trialButton}
                {role === 'admin' && (
                  <>
                    <button 
                      onClick={() => navigate('/administrator')}
                      className="flex items-center gap-2 px-7 py-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow text-lg"
                    >
                      <img src={cogIcon} alt="Settings" className="w-8 h-8" />
                      Go to Settings
                    </button>
                    {!APP_URL.includes('app') && (
                      <button 
                        onClick={() => navigate('/logengine')}
                        className="flex items-center gap-2 px-7 py-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow text-lg"
                      >
                        <img src={logEngineIcon} alt="Log Engine" className="w-8 h-8" />
                        Log Engine
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Someone on the trial allowlist whose own account has limited
                access still gets the one button they came for. */}
            {isAccessLimited && role !== 'admin' && trialButton && (
              <div className="flex gap-5">{trialButton}</div>
            )}

            {isDevPromptTool && (
              <div className="mt-10 max-w-3xl border border-gray-200 rounded-lg p-6 bg-gray-50 relative" style={{ zIndex: 1 }}>
                <h2 className="text-xl font-semibold mb-1">Dev: Prompt Override</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Pick a case study and avatar, load the current prompt, edit it, and save.
                  The edited prompt is used only for that avatar in that case study, only for
                  your next launch on this browser. Nothing is saved to the database or backend.
                </p>

                {ovActive && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-800 text-sm flex items-center justify-between">
                    <span>
                      Active override: <strong>{ovActive.personality}</strong> in{' '}
                      <strong>{ovActive.caseStudy}</strong>
                    </span>
                    <button onClick={clearOverride} className="ml-4 px-3 py-1 bg-white border border-green-300 rounded hover:bg-green-100">
                      Clear
                    </button>
                  </div>
                )}

                <div className="flex gap-3 mb-3">
                  <select
                    value={ovCaseStudy}
                    onChange={(e) => setOvCaseStudy(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded bg-white"
                  >
                    <option value="">Select case study…</option>
                    {CASE_STUDIES.map((cs) => (
                      <option key={cs} value={cs}>{cs}</option>
                    ))}
                  </select>
                  <select
                    value={ovAvatar}
                    onChange={(e) => setOvAvatar(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded bg-white"
                  >
                    {CASE_STUDY_AVATARS.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                  <button
                    onClick={loadCurrentPrompt}
                    disabled={ovLoading}
                    className="px-4 py-2 bg-white border border-gray-300 rounded hover:shadow-md disabled:opacity-50"
                  >
                    {ovLoading ? 'Loading…' : 'Load current prompt'}
                  </button>
                </div>

                <textarea
                  value={ovPrompt}
                  onChange={(e) => setOvPrompt(e.target.value)}
                  placeholder="Load a prompt to see and edit it here…"
                  rows={14}
                  className="w-full px-3 py-2 border border-gray-300 rounded font-mono text-sm bg-white"
                />

                <div className="flex items-center gap-3 mt-3">
                  <button
                    onClick={saveOverride}
                    className="px-4 py-2 bg-[#6c63ff] text-white rounded hover:opacity-90"
                  >
                    Save override
                  </button>
                  {ovStatus && <span className="text-sm text-gray-600">{ovStatus}</span>}
                </div>
              </div>
            )}
          </>
        )}
      </main>
      <div 
        className="fixed inset-0 bg-no-repeat bg-right-bottom pointer-events-none"
        style={{
          backgroundImage: `url(${aiBackground})`,
          backgroundSize: '50% auto',
          backgroundAttachment: 'fixed',
          zIndex: 0
        }}
      ></div>
      
    </div>
  );
};

export default Base;

